/**
 * Card Status Cache
 *
 * Two-tier caching (in-memory Map + localStorage with 24h TTL).
 * Batch-debounced network requests (30ms window, max 100 per call).
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface CardStatus {
  game_changer: boolean;
  banned: boolean;
  unreleased: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────────

const LS_KEY = 'mtgagent_csc_v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 30;
const MAX_BATCH = 100;

// ── State ────────────────────────────────────────────────────────────────────

const mem = new Map<string, CardStatus>();

interface PendingCallback {
  resolve: (s: CardStatus) => void;
  reject: (err: unknown) => void;
}

const pending = new Map<string, PendingCallback[]>();
let timer: ReturnType<typeof setTimeout> | null = null;

// ── localStorage helpers ─────────────────────────────────────────────────────

interface StoredEntry {
  gc: boolean;
  b: boolean;
  u: boolean;
  ts: number;
}

function loadStorage(): void {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const stored: Record<string, StoredEntry> = JSON.parse(raw);
    const now = Date.now();
    for (const [key, entry] of Object.entries(stored)) {
      if (entry.ts && now - entry.ts < TTL_MS) {
        mem.set(key, { game_changer: Boolean(entry.gc), banned: Boolean(entry.b), unreleased: Boolean(entry.u) });
      }
    }
  } catch {
    // corrupt or quota exceeded — ignore
  }
}

function saveToStorage(entries: Record<string, CardStatus>): void {
  try {
    let stored: Record<string, StoredEntry> = {};
    try { stored = JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { /* ignore */ }
    const now = Date.now();
    // Prune expired
    for (const key of Object.keys(stored)) {
      if (!stored[key].ts || now - stored[key].ts >= TTL_MS) delete stored[key];
    }
    for (const [key, status] of Object.entries(entries)) {
      stored[key] = { gc: status.game_changer, b: status.banned, u: status.unreleased, ts: now };
    }
    localStorage.setItem(LS_KEY, JSON.stringify(stored));
  } catch {
    // storage full — degrade gracefully
  }
}

// Seed from localStorage on module load
loadStorage();

// ── Batch fetch ──────────────────────────────────────────────────────────────

function flush(): void {
  timer = null;
  if (!pending.size) return;

  const snapshot = new Map(pending);
  pending.clear();

  const allNames = Array.from(snapshot.keys());

  // Split into batches of MAX_BATCH
  const batches: string[][] = [];
  for (let i = 0; i < allNames.length; i += MAX_BATCH) {
    batches.push(allNames.slice(i, i + MAX_BATCH));
  }

  for (const names of batches) {
    fetch(`/api/v1/cards/status?names=${encodeURIComponent(names.join(','))}`, {
      credentials: 'same-origin',
    })
      .then(r => r.ok ? r.json() : { results: {} })
      .then((data: { results?: Record<string, CardStatus> }) => {
        const results = data.results || {};
        const toStore: Record<string, CardStatus> = {};
        for (const key of names) {
          const match = Object.keys(results).find(k => k.toLowerCase() === key);
          const status: CardStatus = match
            ? results[match]
            : { game_changer: false, banned: false, unreleased: false };
          mem.set(key, status);
          toStore[key] = status;
        }
        saveToStorage(toStore);
        for (const key of names) {
          const callbacks = snapshot.get(key);
          if (callbacks) {
            const status = mem.get(key) || { game_changer: false, banned: false, unreleased: false };
            for (const cb of callbacks) cb.resolve(status);
          }
        }
      })
      .catch(err => {
        for (const key of names) {
          const callbacks = snapshot.get(key);
          if (callbacks) {
            for (const cb of callbacks) cb.reject(err);
          }
        }
      });
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Synchronous cache lookup. Returns cached status or null. */
export function getStatusSync(name: string): CardStatus | null {
  return mem.get(name.toLowerCase()) || null;
}

/** Async lookup with 30ms batch debounce. */
export function getStatus(name: string): Promise<CardStatus> {
  const key = name.toLowerCase();
  const cached = mem.get(key);
  if (cached) return Promise.resolve(cached);

  if (!pending.has(key)) pending.set(key, []);
  return new Promise<CardStatus>((resolve, reject) => {
    pending.get(key)!.push({ resolve, reject });
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, DEBOUNCE_MS);
  });
}

/** Pre-populate cache from deck card metadata to avoid redundant fetches. */
export function seedFromMetadata(
  cardMetadata: Record<string, { game_changer?: boolean; banned?: boolean; unreleased?: boolean }> | null | undefined,
): void {
  if (!cardMetadata) return;
  const toStore: Record<string, CardStatus> = {};
  for (const [name, meta] of Object.entries(cardMetadata)) {
    const key = name.toLowerCase();
    if (!mem.has(key)) {
      const status: CardStatus = {
        game_changer: Boolean(meta.game_changer),
        banned: Boolean(meta.banned),
        unreleased: Boolean(meta.unreleased),
      };
      mem.set(key, status);
      toStore[key] = status;
    }
  }
  if (Object.keys(toStore).length) saveToStorage(toStore);
}

/** Render badge HTML string for a given status. */
export function renderBadges(status: CardStatus | null): string {
  if (!status) return '';
  const parts: string[] = [];
  if (status.game_changer) {
    parts.push('<span class="dlw-badge-gc" title="Game Changer (bracket-restricted)">GC</span>');
  }
  if (status.banned) {
    parts.push('<span class="dlw-badge-banned" title="Banned in Commander">B</span>');
  }
  if (status.unreleased) {
    parts.push('<span class="dlw-badge-unreleased" title="Card not yet released">Unreleased</span>');
  }
  return parts.length ? `<span class="dlw-indicators">${parts.join('')}</span>` : '';
}
